import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import themed from 'webapp/decorators/themed';
import didUpdate from 'webapp/modifiers/did-update';
import FormInputComponent from 'webapp/components/form/input';

export interface FormInputDecimalComponentArgs {
	value: string | null;
	onChange: (value: number | null) => void;
	maxLength?: number;
	name?: string;
	placeholder?: string;
	className?: string;
	focusedHelperText?: string;
	isRequired?: boolean;
	isDisabled?: boolean;
	forceUpdateFromValue?: boolean;
	loseFocusOnEnter?: boolean;
	size?: string;
	theme?: string;
	onEmpty?: (prevValue: unknown) => void;
	insertNewline?: () => void;
	onFocusIn?: () => void;
	onFocusOut?: () => void;
}

interface FormInputDecimalComponentInterface<T> {
	Element: HTMLDivElement; // this is needed for ...attributes
	Args: T;
}

const VALID_INPUT_REGEX = /^(-?\d+\.?\d*|-)$/;
const ONLY_ZEROS_AFTER_DECIMAL_REGEX = /^-?\d+\.\d*0+$/;

@themed()
export default class FormInputDecimalComponent<
	T extends FormInputDecimalComponentArgs,
> extends Component<FormInputDecimalComponentInterface<T>> {
	@service('account') declare accountService: Service.Account;

	@tracked oldValue = 0;
	@tracked isFocused = false;

	declare themeClass: string; // from @themed

	guid = guidFor(this);

	constructor(owner: Owner, args: T) {
		super(owner, args);

		this.formattedValue = this.args.value;

		if (this.formattedValue) {
			this.oldValue = +this.formattedValue;
		}
	}

	get name() {
		return `${this.args.name || 'input-decimal'}-${this.guid}`;
	}

	@tracked _formattedValue = '';
	set formattedValue(value: string | null) {
		this._formattedValue = this.getFormattedValueFromValue(value);
	}

	get formattedValue(): string {
		return this._formattedValue;
	}

	getFormattedValueFromValue(newValue: string | null): string {
		let value = newValue;

		if (value === null || value === undefined) {
			value = '';
		}

		if (String(value).includes('e')) {
			return String(this.oldValue);
		}

		return String(value);
	}

	removeTrailingZeros(value: string): string {
		if (value.length === 2 && value.startsWith('0') && value.charAt(1) !== '.') {
			return value.substring(1);
		}

		return value;
	}

	getMinusInFront(value: string) {
		if (value.endsWith('-') && value.length > 1) {
			value = /(-*)([^-]*)/.exec(value)?.[2] || '';

			return '-' + value.substring(0, value.length);
		}

		return value;
	}

	calculateAndGetNumeral(): number | null {
		let value = this.formattedValue || '';
		value = this.removeTrailingZeros(value);
		value = this.getMinusInFront(value);
		let numeralValue: number | null = this.accountService.parseAmericanNumber(value);
		const isInputValid = VALID_INPUT_REGEX.test(value) || value === '',
			valueOnlyZerosAfterDecimal = ONLY_ZEROS_AFTER_DECIMAL_REGEX.test(value);

		if (!isInputValid) {
			this.formattedValue = String(this.oldValue);

			numeralValue = this.accountService.parseAmericanNumber(this.formattedValue);
		} else if (valueOnlyZerosAfterDecimal) {
			this.oldValue = +this.formattedValue;
		} else {
			if (!!this.args.isRequired && !value && !value.startsWith('-')) {
				value = '0';
				numeralValue = 0;
			}

			this.formattedValue = value;
			this.oldValue = +value;
		}

		return numeralValue;
	}

	@action
	forceUpdateOnValueChange(): void {
		// otherwise we cannot influence on the value from outside
		// becase of the fact that "value" is not used after first input change
		this.formattedValue = this.args.value;
	}

	@action
	change(): void {
		if (this.args.onChange) {
			this.args.onChange(this.calculateAndGetNumeral());
		}
	}

	@action
	focusIn(): void {
		this.isFocused = true;
		if (this.args.onFocusIn) {
			this.args.onFocusIn();
		}
	}

	@action
	focusOut(): void {
		this.isFocused = false;

		if (this.args.onFocusOut) {
			this.args.onFocusOut();
		}
	}
	<template>
		<div
			class="input-decimal {{this.themeClass}}"
			{{didUpdate this.forceUpdateOnValueChange @forceUpdateFromValue}}
		>

			<FormInputComponent
				name={{this.name}}
				maxlength={{@maxLength}}
				@value={{this.formattedValue}}
				class={{@className}}
				@input={{this.change}}
				placeholder={{@placeholder}}
				disabled={{@isDisabled}}
				@hideFloatLabel={{true}}
				@loseFocusOnEnter={{true}}
				@focusIn={{this.focusIn}}
				@focusOut={{this.focusOut}}
				@insertNewline={{@insertNewline}}
				@theme={{@theme}}
				@size={{@size}}
				@onEmpty={{@onEmpty}}
			/>

			{{#if @focusedHelperText}}
				<div
					class="helper-text {{if this.isFocused 'helper-text--show'}}"
				>{{@focusedHelperText}}
				</div>
			{{/if}}

		</div>
	</template>
}
